import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  DesignSystem,
  DesignSystemsStore,
  DesignTokens,
  LegacyDesignSystemState,
  MotifId,
} from '../types'
import { copyMotifTokens, getMotifById, DEFAULT_TOKENS } from '../data/motifs'

const STORAGE_KEY = 'nimble-design-systems-v2'
const LEGACY_STORAGE_KEY = 'nimble-design-system'

// Generate a unique ID
function generateId(): string {
  return `ds-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

// Get current ISO timestamp
function getTimestamp(): string {
  return new Date().toISOString()
}

// Convert legacy font family to new typography structure
function migrateLegacyFontFamily(
  fontFamily: 'system' | 'inter' | 'mono'
): DesignTokens['typography'] {
  switch (fontFamily) {
    case 'inter':
      return {
        displayFont: { family: 'Inter', weight: 'semibold', size: '2xl' },
        bodyFont: { family: 'Inter', weight: 'normal', size: 'base' },
        monoFont: { family: 'Source Code Pro' },
      }
    case 'mono':
      return {
        displayFont: { family: 'JetBrains Mono', weight: 'bold', size: '2xl' },
        bodyFont: { family: 'JetBrains Mono', weight: 'normal', size: 'base' },
        monoFont: { family: 'JetBrains Mono' },
      }
    default:
      return {
        displayFont: { family: 'System UI', weight: 'bold', size: '2xl' },
        bodyFont: { family: 'System UI', weight: 'normal', size: 'base' },
        monoFont: { family: 'Source Code Pro' },
      }
  }
}

// Migrate legacy heading weight to new FontWeight type
function migrateLegacyHeadingWeight(
  weight: 'medium' | 'semibold' | 'bold' | 'extrabold'
): DesignTokens['typography']['displayFont']['weight'] {
  return weight
}

// Migrate legacy state to new format
function migrateLegacyState(legacy: LegacyDesignSystemState): DesignSystem {
  const motif = getMotifById(legacy.baseMotifId)
  const baseTokens = motif ? copyMotifTokens(legacy.baseMotifId as MotifId) : DEFAULT_TOKENS

  // Apply overrides
  const tokens: DesignTokens = {
    ...baseTokens,
    primaryColor: legacy.overrides.primaryColor ?? baseTokens.primaryColor,
    secondaryColor: legacy.overrides.secondaryColor ?? baseTokens.secondaryColor,
    neutralColor: legacy.overrides.neutralColor ?? baseTokens.neutralColor,
    borderRadius: legacy.overrides.borderRadius ?? baseTokens.borderRadius,
    shadow: legacy.overrides.shadow ?? baseTokens.shadow,
  }

  // Handle typography migration
  if (legacy.overrides.fontFamily || legacy.overrides.headingWeight) {
    const typography = legacy.overrides.fontFamily
      ? migrateLegacyFontFamily(legacy.overrides.fontFamily)
      : { ...baseTokens.typography }

    if (legacy.overrides.headingWeight) {
      typography.displayFont.weight = migrateLegacyHeadingWeight(legacy.overrides.headingWeight)
    }

    tokens.typography = typography
  }

  const timestamp = getTimestamp()

  return {
    id: generateId(),
    name: legacy.themeName || 'Migrated Theme',
    tokens,
    createdAt: timestamp,
    updatedAt: timestamp,
  }
}

// Load from localStorage with migration
function loadStore(): DesignSystemsStore {
  if (typeof window === 'undefined') {
    return { systems: [], activeSystemId: null }
  }

  try {
    // Check for v2 storage first
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }

    // Check for legacy storage and migrate
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (legacy) {
      const legacyState: LegacyDesignSystemState = JSON.parse(legacy)
      const migratedSystem = migrateLegacyState(legacyState)

      const newStore: DesignSystemsStore = {
        systems: [migratedSystem],
        activeSystemId: migratedSystem.id,
      }

      // Save migrated data and remove legacy
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newStore))
      localStorage.removeItem(LEGACY_STORAGE_KEY)

      return newStore
    }
  } catch {
    // Ignore parse errors
  }

  return { systems: [], activeSystemId: null }
}

// Save to localStorage
function saveStore(store: DesignSystemsStore): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
}

export function useDesignSystems() {
  const [store, setStore] = useState<DesignSystemsStore>(loadStore)

  // Persist to localStorage when store changes
  useEffect(() => {
    saveStore(store)
  }, [store])

  // Get the active system
  const activeSystem = useMemo(() => {
    if (!store.activeSystemId) return null
    return store.systems.find((s) => s.id === store.activeSystemId) ?? null
  }, [store.systems, store.activeSystemId])

  // Create a new system from a motif
  const createSystem = useCallback((name: string, motifId: MotifId): DesignSystem => {
    const tokens = copyMotifTokens(motifId)
    const timestamp = getTimestamp()
    const newSystem: DesignSystem = {
      id: generateId(),
      name,
      tokens,
      createdAt: timestamp,
      updatedAt: timestamp,
    }

    setStore((prev) => ({
      systems: [...prev.systems, newSystem],
      activeSystemId: newSystem.id,
    }))

    return newSystem
  }, [])

  // Delete a system
  const deleteSystem = useCallback((id: string) => {
    setStore((prev) => {
      const filtered = prev.systems.filter((s) => s.id !== id)
      let newActiveId = prev.activeSystemId

      // If we deleted the active system, switch to another
      if (prev.activeSystemId === id) {
        newActiveId = filtered.length > 0 ? filtered[0].id : null
      }

      return {
        systems: filtered,
        activeSystemId: newActiveId,
      }
    })
  }, [])

  // Rename a system
  const renameSystem = useCallback((id: string, name: string) => {
    setStore((prev) => ({
      ...prev,
      systems: prev.systems.map((s) =>
        s.id === id ? { ...s, name, updatedAt: getTimestamp() } : s
      ),
    }))
  }, [])

  // Set the active system
  const setActiveSystem = useCallback((id: string) => {
    setStore((prev) => ({
      ...prev,
      activeSystemId: id,
    }))
  }, [])

  // Update a token in the active system
  const updateToken = useCallback(
    <K extends keyof DesignTokens>(key: K, value: DesignTokens[K]) => {
      if (!store.activeSystemId) return

      setStore((prev) => ({
        ...prev,
        systems: prev.systems.map((s) =>
          s.id === prev.activeSystemId
            ? {
                ...s,
                tokens: { ...s.tokens, [key]: value },
                updatedAt: getTimestamp(),
              }
            : s
        ),
      }))
    },
    [store.activeSystemId]
  )

  // Update typography in the active system
  const updateTypography = useCallback(
    <K extends keyof DesignTokens['typography']>(
      key: K,
      value: DesignTokens['typography'][K]
    ) => {
      if (!store.activeSystemId) return

      setStore((prev) => ({
        ...prev,
        systems: prev.systems.map((s) =>
          s.id === prev.activeSystemId
            ? {
                ...s,
                tokens: {
                  ...s.tokens,
                  typography: { ...s.tokens.typography, [key]: value },
                },
                updatedAt: getTimestamp(),
              }
            : s
        ),
      }))
    },
    [store.activeSystemId]
  )

  // Duplicate a system
  const duplicateSystem = useCallback((id: string): DesignSystem | null => {
    const system = store.systems.find((s) => s.id === id)
    if (!system) return null

    const timestamp = getTimestamp()
    const newSystem: DesignSystem = {
      id: generateId(),
      name: `${system.name} (Copy)`,
      tokens: JSON.parse(JSON.stringify(system.tokens)),
      createdAt: timestamp,
      updatedAt: timestamp,
    }

    setStore((prev) => ({
      systems: [...prev.systems, newSystem],
      activeSystemId: newSystem.id,
    }))

    return newSystem
  }, [store.systems])

  // Reset active system to a motif's tokens
  const resetToMotif = useCallback((motifId: MotifId) => {
    if (!store.activeSystemId) return

    const tokens = copyMotifTokens(motifId)

    setStore((prev) => ({
      ...prev,
      systems: prev.systems.map((s) =>
        s.id === prev.activeSystemId
          ? { ...s, tokens, updatedAt: getTimestamp() }
          : s
      ),
    }))
  }, [store.activeSystemId])

  return {
    systems: store.systems,
    activeSystem,
    activeSystemId: store.activeSystemId,
    createSystem,
    deleteSystem,
    renameSystem,
    setActiveSystem,
    updateToken,
    updateTypography,
    duplicateSystem,
    resetToMotif,
    hasNoSystems: store.systems.length === 0,
  }
}
